import React, { useCallback, useRef } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { ErrorFlagRenderer } from '@atlassian/jira-error-boundary-flag-renderer/src/ErrorBoundaryFlag.tsx';
import {
	ForgeAppsContainer,
	useForgeApps,
} from '@atlassian/jira-forge-action-apps-controller/src/index.tsx';
import { DropdownTrigger } from '@atlassian/entry-point-dropdown';
import { PluggableItemsCacheProvider } from '../controllers/pluggable-items-cache/index.tsx';
import {
	useInlineDialogZIndexOverrideLayoutEffect,
	useModalDialogCleanupEffect,
	useRegisterModalDialogAnalytics,
} from '../common/dialog-controls.tsx';
import { appsButtonEntryPoint } from './entrypoint.tsx';
import { PluggableItemsFilterButton } from './filter-button/index.tsx';
import { IssueNavigatorActionRuntimeContainer } from './forge-runtime-container/index.tsx';

const errorBoundaryId = 'pluggable-items-entrypoint';

const PluggableItems = () => {
	const [, { closeApps }] = useForgeApps();

	const isRegistered = useRef(false);
	const registerModalDialogAnalytics = useRegisterModalDialogAnalytics();
	const registerModalDialogAnalyticsOnce = useCallback(() => {
		if (!isRegistered.current) {
			registerModalDialogAnalytics();
			isRegistered.current = true;
		}
	}, [isRegistered, registerModalDialogAnalytics]);

	useModalDialogCleanupEffect();
	useInlineDialogZIndexOverrideLayoutEffect();

	return (
		<JSErrorBoundary
			id={errorBoundaryId}
			packageName="jiraIssueNavigatorActionPluggableItems"
			teamName="empanada"
			fallback={({ error }) => (
				<>
					<PluggableItemsFilterButton isDisabled />
					<ErrorFlagRenderer id={errorBoundaryId} error={error} />
				</>
			)}
		>
			<PluggableItemsCacheProvider onDataChanged={closeApps}>
				<DropdownTrigger
					entryPoint={appsButtonEntryPoint}
					errorFallback={({ error }) => {
						// Throw error back up the stack to be caught and logged by the JSErrorBoundary
						throw error;
					}}
					trigger={(triggerProps) => (
						<PluggableItemsFilterButton
							{...triggerProps}
							onClick={(...onClickProps) => {
								registerModalDialogAnalyticsOnce();
								triggerProps.onClick?.(...onClickProps);
							}}
						/>
					)}
					testId="issue-navigator-action-pluggable-items.ui.filter-button"
					shouldFlip
					placement="bottom-end"
					spacing="compact"
					shouldRenderToParent
				/>
			</PluggableItemsCacheProvider>
			<IssueNavigatorActionRuntimeContainer />
		</JSErrorBoundary>
	);
};

const PluggableItemsWithForge = (): React.JSX.Element => (
	<ForgeAppsContainer scope="issue-navigator-action">
		<PluggableItems />
	</ForgeAppsContainer>
);

export default PluggableItemsWithForge;
